/**
 * rock_mesh.ts — PROCEDURAL rock geometry for the geology layer (2026-09-25, visual-realism R3).
 *
 * No generated assets and no credits: every cliff face, outcrop, boulder, talus rock and pebble is
 * built here from a seed, deterministically, once per (kind, variant) per session:
 *   1. a watertight CUBE-SPHERE (n × n per face, shared seams) pushed out to a SUPERELLIPSOID —
 *      boxy for cliff slabs, rounder for boulders;
 *   2. anisotropic scale to the kind's dimensions;
 *   3. FRACTURE PLANES: K random planes clip the body (every point past a plane is projected onto
 *      it) — the flat, angular facets that make a lump read as broken rock rather than a potato;
 *   4. 3D value-noise displacement along the surface normal, and on cliffs and outcrops STRATA:
 *      horizontal ledges from a triangle wave in height (bedding planes 0.6–1.4 m apart);
 *   5. the base flattened, the origin at the bottom centre (min y = 0), smooth normals.
 * Triangle budgets (the research's table): cliff 3,072 · outcrop 1,728 · boulder 972 · talus 300 ·
 * pebble 108. A low-detail twin (`rockColliderMesh`) is the collider (n = 4, 192 triangles).
 * Pure over three's BufferGeometry: the harness builds them in node.
 */
import * as THREE from 'three/webgpu';
import { ROCK_SPEC, rockShape, type RockKind } from './rock_shape.js';
export { ROCK_KINDS, ROCK_SPEC, rockHeight, rng, noise3, type RockKind } from './rock_shape.js';

function buildRock(kind: RockKind, variant: number, n: number): THREE.BufferGeometry {
  const { pos, idx } = rockShape(kind, variant, n);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setIndex(n * n * 6 * 6 > 65535 ? new THREE.BufferAttribute(Uint32Array.from(idx), 1) : new THREE.BufferAttribute(Uint16Array.from(idx), 1));
  g.computeVertexNormals();
  g.computeBoundingBox(); g.computeBoundingSphere();
  g.name = `rock:${kind}:${variant}`;
  return g;
}

const cache = new Map<string, THREE.BufferGeometry>();
/** The render geometry of (kind, variant) — built once, shared (never dispose it per instance). */
export function rockGeometry(kind: RockKind, variant: number): THREE.BufferGeometry {
  const v = ((variant % ROCK_SPEC[kind].variants) + ROCK_SPEC[kind].variants) % ROCK_SPEC[kind].variants;
  const k = `${kind}:${v}`;
  let g = cache.get(k);
  if (!g) { g = buildRock(kind, v, ROCK_SPEC[kind].n); cache.set(k, g); }
  return g;
}
const colCache = new Map<string, { positions: number[]; indices: number[] }>();
/** The same rock at n = 4 (192 triangles), model space — the collider's source. */
export function rockColliderMesh(kind: RockKind, variant: number): { positions: number[]; indices: number[] } {
  const v = ((variant % ROCK_SPEC[kind].variants) + ROCK_SPEC[kind].variants) % ROCK_SPEC[kind].variants;
  const k = `${kind}:${v}`;
  let c = colCache.get(k);
  if (!c) {
    const g = buildRock(kind, v, 4);
    c = { positions: Array.from(g.getAttribute('position').array as Float32Array), indices: Array.from(g.index!.array as ArrayLike<number>) };
    g.dispose(); colCache.set(k, c);
  }
  return c;
}
/** Triangles of a kind's render mesh. */
export function rockTris(kind: RockKind): number { const n = ROCK_SPEC[kind].n; return n * n * 12; }
